import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';

import {
  CreditCard,
  CreditCardPlans,
  ErrorToken,
  Payment,
  PaymentResponseInfo,
  PaymentResult,
  ResponseApi,
  SpsResponsePayment,
} from '../models/payment.model';
import { SpsDecidirApiService } from '../services/sps-decidir.api.service';

declare var Decidir: any;

@Component({
  selector: 'sps-form-pay',
  templateUrl: './sps-decidir.component.html',
  styleUrls: ['./sps-decidir.component.scss']
})
export class SpsDecidirComponent implements OnInit, OnChanges {

  @ViewChild('f') checkOutForm: NgForm;
  @Input() accessToken: string;
  @Input() spsUrlApi: string;
  @Input() vkUrlApi: string;
  @Input() paymentAmount: string;
  @Input() logoSps: string;
  @Input() currency: string;
  @Input() publishableKey: string;
  @Input() successUrl: string;
  @Input() failureUrl: string;
  @Input() useCybersource: boolean;
  @Input() debugLog: boolean;
  @Input() storeId: string;
  @Input() idVen: number;
  @Input() data: string;
  @Input() labelButton: string;
  @Input() language = 'es';
  @Output() statusPay = new EventEmitter<PaymentResult>();

  disabled = false;
  totalAmount: string;
  totalFinancedAmount: string;
  currentTEA: string;
  currentCFT: string;

  creditCards: CreditCard[];
  selectCreditCardPlan: CreditCardPlans = null;
  creditCardPlans: CreditCardPlans[];
  paymentResult: PaymentResult = { success: false, paymentId: '', integrator: 'DECIDIR' };

  // Form bind
  monthText: string;
  yearText: string;
  cvcText: string;
  dniText: string;
  payerNameText: string;
  cardText: string;
  submitPressed = false;
  invalidDate = false;

  private decidir: any;
  addScript = true;

  paymentState = 'unsubmitted';
  payerName: string;
  issuer?: number = null;
  payment: Payment;
  paymentSuccessInfo: PaymentResponseInfo;
  errorMessage: string;

  constructor(private router: Router, public spsDecidirApiService: SpsDecidirApiService) { }

  ngOnInit() {
    this.initDecidir();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.paymentAmount.isFirstChange()) {
      this.checkOutForm.reset();
      this.disabled = false;
    }
  }

  private initDecidir() {
    this.addDecidirScript()
      .then(() => {
        this.getCreditCards();
        this.initializeSpsDecidir();
      });
  }

  addDecidirScript() {
    this.addScript = true;
    return new Promise((resolve) => {
      const scripttagElement = document.createElement('script');
      scripttagElement.src = 'https://live.decidir.com/static/v2.4/decidir.js';
      scripttagElement.onload = resolve;
      document.body.appendChild(scripttagElement);
    });
  }

  private getCreditCards() {
    this.spsDecidirApiService.getCreditCards(this.accessToken, this.vkUrlApi)
      .subscribe((res: ResponseApi) => {
        this.creditCards = res.data as CreditCard[];
        this.logger('getCreditCards', this.creditCards);
      }, _ => this.paymentState = 'apiError');
  }

  private getPlansByCreditCardId(amount: string, creditCardId: number) {
    this.spsDecidirApiService.getPlansByCreditCardId(this.accessToken, this.vkUrlApi, amount, creditCardId)
      .subscribe((res: ResponseApi) => {
        this.creditCardPlans = res.data as CreditCardPlans[];
        this.logger('getPlansByCreditCardId', this.creditCardPlans);
      },_ => this.paymentState = 'apiError');
  }

  private initializeSpsDecidir() {
    this.decidir = new Decidir(this.spsDecidirApiService.normalizeUrl(this.spsUrlApi), !this.useCybersource);
    this.decidir.setTimeout(15000);
    this.decidir.setPublishableKey(this.publishableKey);
  }

  private logger(message: string, data: any): void {
    if (!this.debugLog) { return; }
    console.log(message, data);
  }

  public selectedCreditCard() {
    if (!this.issuer) { return; }

    this.selectCreditCardPlan = null;
    if (this.currency === 'ARS') {
      this.getPlansByCreditCardId(this.paymentAmount, this.issuer);
      return;
    }

    if (this.currency === 'USD') {
      this.totalAmount = this.totalFinancedAmount = this.paymentAmount;
    }
  }

  selectedCreditCardPlan() {
    if (this.selectCreditCardPlan === null) { return; }

    this.totalFinancedAmount = this.selectCreditCardPlan.totalAmount;
    this.totalAmount = this.paymentAmount;
    this.currentTEA = this.selectCreditCardPlan.tna;
    this.currentCFT = this.selectCreditCardPlan.cft;
  }

  onSubmit(event) {
    event.preventDefault();
    this.submitPressed = true;
    this.paymentResult = { success: false, paymentId: '', integrator: 'DECIDIR' };

    if (!this.checkOutForm.valid) { return; }

    this.paymentState = 'processing';
    const form = document.querySelector('#formulario');
    this.decidir.createToken(form, (status: number, response: SpsResponsePayment) => {
      if (status !== 200 && status !== 201) {
        this.paymentState = 'error';
        this.statusPay.emit(this.paymentResult);
        this.processErrorToken(response);
      } else {
        this.makePayment(form, response);
      }
    });
  }

  makePayment(form: any, paymentResponse: SpsResponsePayment): void {
    this.logger('makePayment:', JSON.stringify(paymentResponse));

    this.payment = {
      token: paymentResponse.id,
      amount: this.totalAmount,
      totalFinancedAmount: this.totalFinancedAmount,
      bin: paymentResponse.bin,
      currency: 'ARS',
      documentNumber: this.currency === 'USD' ? '0' : paymentResponse.cardholder.identification.number,
      idVen: this.idVen,
      paymentType: 'single',
      installments: this.currency === 'USD' ? 1 : this.selectCreditCardPlan.installments,
      creditCardPlanId: this.currency === 'USD' ? 0 : this.selectCreditCardPlan.id,
      paymentMethodId: this.issuer,
      cardholder: this.payerNameText,
      storeId: this.storeId,
      data: this.data,
      subPayments: []
    };

    this.logger('payment:', JSON.stringify(this.payment));
    this.spsDecidirApiService.makePayment(this.payment, this.accessToken, this.vkUrlApi)
      .subscribe((res: ResponseApi) => {
        this.paymentResponseHandler(res);
      }, error => {
        this.statusPay.emit(this.paymentResult);
        this.paymentState = 'apiError';
        console.error(error);
      });
  }

  paymentResponseHandler(response: ResponseApi) {
    this.logger('paymentResponseHandler:', JSON.stringify(response));

    if (response.success) {
      this.paymentState = 'success';
      this.paymentSuccessInfo = response.data as PaymentResponseInfo;
      this.paymentResult = { success: true, paymentId: this.paymentSuccessInfo.payment_id, integrator: 'DECIDIR' };
      this.statusPay.emit(this.paymentResult);
      this.disabled = true;

      if (this.successUrl && this.successUrl.length > 0) {
        this.router.navigate([`${this.successUrl}`]).then();
        return;
      }
    } else {
      this.paymentState = 'error';
      this.statusPay.emit(this.paymentResult);
      this.processErrorMessage(response.data);

      if (this.failureUrl && this.failureUrl.length > 0) {
        this.router.navigate([`${this.failureUrl}`]).then()
        return;
      }
    }
  }

  processErrorToken(response: any): void {
    const error: ErrorToken = response as ErrorToken;

    // obtenemos mensajes de error
    error.error.forEach((x) => {
      if (x.error.type === 'invalid_expiry_date') {
        this.errorMessage = 'RevisÃ¡ la fecha de vencimiento.';
      } else {
        this.errorMessage = JSON.stringify(error);
      }
    });
  }

  // diferentes tipos de error nos devuelve decidir
  processErrorMessage(responseError: PaymentResponseInfo): void {
    if (responseError.error) {
      switch (responseError.error.type) {
        case 'invalid_card':
          this.errorMessage = 'Tarjeta invalida';
          break;
        case 'request_authorization_card':
          this.errorMessage = 'Pedir autorizaciÃ³n';
          break;
        default:
          this.errorMessage = JSON.stringify(responseError.error.reason);
          break;
      }
    }

    if (responseError.error_type) {
      switch (responseError.error_type) {
        case 'invalid_request_error':
          if (responseError.validation_errors.length > 0) {
            if (responseError.validation_errors[0].code === 'invalid_param') {
              switch (responseError.validation_errors[0].param) {
                case 'card_number':
                  this.errorMessage = 'Tarjeta invÃ¡lida';
                  break;
                case 'bin':
                  this.errorMessage = 'Tarjeta invÃ¡lida';
                  break;
                default:
                  this.errorMessage = JSON.stringify(responseError);
                  break;
              }
            }
          }
          break;
        case 'not_found_error':
          this.errorMessage = 'Datos no encontrados (404).';
          break;
        case 'authentication_error':
          this.errorMessage = 'Error (401).';
          break;
        case 'malformed_request_error':
          this.errorMessage = 'Error al enviar los datos  (400).';
          break;
        case 'api_error':
          this.errorMessage = 'Error interno (409).';
          break;
        default:
          this.errorMessage = JSON.stringify(responseError);
          break;
      }
    }
  }

  compareDate(year: number, month: number, day: number): number {
    const d1: Date = new Date(year, month, day);
    const d2: Date = new Date();

    const same = d1.getTime() === d2.getTime();
    if (same) { return 0; }
    if (d1 > d2) { return 1; }
    if (d1 < d2) { return -1; }
  }

  // Input controls
  onYearChange() {
    if (Number.isNaN(+this.yearText)) {
      setTimeout(() => {
        this.yearText = '';
      });
    }
  }

  onYearBlur(element: any) {
    if (this.compareDate(
      Number(`20${this.yearText}`),
      Number(this.monthText),
      new Date().getDate()) < 0) {
      setTimeout(() => {
        this.yearText = '';
        this.invalidDate = true;
        element.target.focus();
      });
    } else {
      this.invalidDate = false;
    }

  }

  onMonthLostFocus() {
    if (+this.monthText > 0 && !isNaN(+this.monthText)) {
      if (this.monthText === '1') {
        this.monthText = '01';
      }
    }
  }

  onMonthChange() {
    if (+this.monthText >= 0 && +this.monthText < 13 && !isNaN(+this.monthText)) {
      switch (this.monthText) {
        case '2': {
          this.monthText = '02';
          break;
        }
        case '3': {
          this.monthText = '03';
          break;
        }
        case '4': {
          this.monthText = '04';
          break;
        }
        case '5': {
          this.monthText = '05';
          break;
        }
        case '6': {
          this.monthText = '06';
          break;
        }
        case '7': {
          this.monthText = '07';
          break;
        }
        case '8': {
          this.monthText = '08';
          break;
        }
        case '9': {
          this.monthText = '09';
          break;
        }
      }
    } else {
      setTimeout(() => {
        this.monthText = '';
      });
    }
  }

  onCodeChange() {
    if (Number.isNaN(+this.cvcText)) {
      setTimeout(() => {
        this.cvcText = '';
      });
    }

    if (this.cvcText.search(' ') > -1) {
      setTimeout(() => {
        this.cvcText = '';
      });
    }
  }

  onDniChange() {
    if (Number.isNaN(+this.dniText)) {
      setTimeout(() => {
        this.dniText = '';
      });
    }

    if (this.dniText.search(' ') > -1) {
      setTimeout(() => {
        this.dniText = '';
      });
    }
  }

  onPayerNameChange() {
    if (!isNaN(+this.payerNameText)) {
      setTimeout(() => {
        this.payerNameText = '';
      });
    }
  }

  onCardChange() {
    if (Number.isNaN(+this.cardText)) {
      setTimeout(() => {
        this.cardText = '';
      });
    }

    if (this.cardText.search(' ') > -1) {
      setTimeout(() => {
        this.cardText = '';
      });
    }

    // setea tarjeta
    this.setCreditType(this.cardText);
  }

  setCreditType(bin: string): void {
    if (bin.length < 6) {
      this.issuer = null;
      return;
    }

    if (bin.length === 6) {
      this.issuer = null;

      // visa debito
      if (bin.substring(0, 4) === '4770' ||
        bin.substring(0, 6) === '442548' ||
        bin.substring(0, 6) === '406809' ||
        bin.substring(0, 6) === '408738' ||
        bin.substring(0, 6) === '411521' ||
        bin.substring(0, 6) === '411663' ||
        bin.substring(0, 6) === '426359' ||
        bin.substring(0, 6) === '429780' ||
        bin.substring(0, 6) === '438267' ||
        bin.substring(0, 6) === '461187' ||
        bin.substring(0, 6) === '500334' ||
        bin.substring(0, 6) === '509413' ||
        bin.substring(0, 6) === '512285' ||
        bin.substring(0, 6) === '512881' ||
        bin.substring(0, 6) === '518635' ||
        bin.substring(0, 6) === '538449' ||
        bin.substring(0, 6) === '550553' ||
        bin.substring(0, 6) === '566057' ||
        bin.substring(0, 6) === '567933' ||
        bin.substring(0, 6) === '572140' ||
        bin.substring(0, 6) === '584257' ||
        bin.substring(0, 6) === '598718' ||
        bin.substring(0, 6) === '605801' ||
        bin.substring(0, 6) === '606120' ||
        bin.substring(0, 6) === '631057' ||
        bin.substring(0, 6) === '676215' ||
        bin.substring(0, 6) === '713274' ||
        bin.substring(0, 6) === '713284' ||
        bin.substring(0, 6) === '717515' ||
        bin.substring(0, 6) === '748761' ||
        bin.substring(0, 6) === '753371' ||
        bin.substring(0, 6) === '753600' ||
        bin.substring(0, 6) === '912394' ||
        bin.substring(0, 6) === '916148' ||
        bin.substring(0, 6) === '916773' ||
        bin.substring(0, 6) === '923405' ||
        bin.substring(0, 6) === '930965' ||
        bin.substring(0, 6) === '934091' ||
        bin.substring(0, 6) === '935761' ||
        bin.substring(0, 6) === '943313' ||
        bin.substring(0, 6) === '945721' ||
        bin.substring(0, 6) === '948809' ||
        bin.substring(0, 6) === '960644' ||
        bin.substring(0, 6) === '969085' ||
        bin.substring(0, 6) === '970167' ||
        bin.substring(0, 6) === '978061' ||
        bin.substring(0, 8) === '40267919' ||
        bin.substring(0, 8) === '40327176' ||
        bin.substring(0, 8) === '40416004' ||
        bin.substring(0, 8) === '40421933' ||
        bin.substring(0, 8) === '40451556' ||
        bin.substring(0, 8) === '40541010' ||
        bin.substring(0, 8) === '40623835' ||
        bin.substring(0, 8) === '40733469' ||
        bin.substring(0, 8) === '40733586' ||
        bin.substring(0, 8) === '40872498' ||
        bin.substring(0, 8) === '45007789' ||
        bin.substring(0, 8) === '45024822' ||
        bin.substring(0, 8) === '45034751' ||
        bin.substring(0, 8) === '45046987' ||
        bin.substring(0, 8) === '45060549' ||
        bin.substring(0, 8) === '45062482' ||
        bin.substring(0, 8) === '45070071' ||
        bin.substring(0, 8) === '45084708' ||
        bin.substring(0, 8) === '45091433' ||
        bin.substring(0, 8) === '45100902' ||
        bin.substring(0, 8) === '45103869' ||
        bin.substring(0, 8) === '45131047' ||
        bin.substring(0, 8) === '45168884' ||
        bin.substring(0, 8) === '45174306' ||
        bin.substring(0, 8) === '45184611' ||
        bin.substring(0, 8) === '45191921' ||
        bin.substring(0, 8) === '45201881' ||
        bin.substring(0, 8) === '45206401' ||
        bin.substring(0, 8) === '45245294' ||
        bin.substring(0, 8) === '45314944' ||
        bin.substring(0, 8) === '45317847' ||
        bin.substring(0, 8) === '45357409' ||
        bin.substring(0, 8) === '45374587' ||
        bin.substring(0, 8) === '45394705' ||
        bin.substring(0, 8) === '45456584' ||
        bin.substring(0, 8) === '45541924' ||
        bin.substring(0, 8) === '45545795' ||
        bin.substring(0, 8) === '45567746' ||
        bin.substring(0, 8) === '45583759' ||
        bin.substring(0, 8) === '45648838' ||
        bin.substring(0, 8) === '45672506' ||
        bin.substring(0, 8) === '45678421' ||
        bin.substring(0, 8) === '45710756' ||
        bin.substring(0, 8) === '45716006' ||
        bin.substring(0, 8) === '45725029' ||
        bin.substring(0, 8) === '45732113' ||
        bin.substring(0, 8) === '45732698' ||
        bin.substring(0, 8) === '45793917' ||
        bin.substring(0, 8) === '45841185' ||
        bin.substring(0, 8) === '45862021' ||
        bin.substring(0, 8) === '45915336' ||
        bin.substring(0, 8) === '45971496') {
        this.issuer = 31;
      }
      // mastercard
      else if (bin.substring(0, 2) === '51' ||
        bin.substring(0, 2) === '52' ||
        bin.substring(0, 2) === '53' ||
        bin.substring(0, 2) === '54' ||
        bin.substring(0, 2) === '55') {
        this.issuer = 15;
      }
      // tarjeta naranja
      else if (bin.substring(0, 6) === '589562') {
        this.issuer = 24;
      }
      // visa credito
      else if (bin.substring(0, 1) === '4') {
        this.issuer = 1;
      }

      this.selectedCreditCard();
    }
  }

  onPaste(): boolean {
    return false;
  }

}
